"use client";

import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent } from "react";
import { createPortal } from "react-dom";
import cnBind from "classnames/bind";
import { usePathname } from "next/navigation";

import type { NavbarItem } from "@/components/NavBar/constants";
import { ArrowDownIcon } from "@/shared/assests/svg/svg";

import styles from "./Navbar.module.scss";

const cx = cnBind.bind(styles);

type NavbarProps = {
    className?: string;
    classNameItems?: string;
    onClick?: () => void;
    data: NavbarItem[];
};

const LinkContent = ({ item, isOpen }: { item: NavbarItem; isOpen?: boolean }) => (
    <>
        <span className={cx("link-label")}>{item.label}</span>
        {item.description && <span className={cx("link-description")}>{item.description}</span>}
        {(item.items?.length ?? 0) > 0 && <ArrowDownIcon className={cx("icon-mob", isOpen && "icon-open")} />}
    </>
);

const NavbarList = ({
    items,
    level = 0,
    onClick,
    forceOpenIndex,
    setForceOpenIndex,
    onMenuPointerEnter,
    onMenuPointerLeave,
    onTopItemClick,
    onBeforeToggle,
    gridLayout = "two-column",
}: {
    items: NavbarItem[];
    level?: number;
    onClick?: () => void;
    forceOpenIndex?: number | null;
    setForceOpenIndex?: (index: number | null) => void;
    onMenuPointerEnter?: () => void;
    onMenuPointerLeave?: () => void;
    onTopItemClick?: (index: number, hasChildren: boolean) => void;
    onBeforeToggle?: () => void;
    gridLayout?: "two-column" | "three-column";
}) => {
    const [hoverIndex, setHoverIndex] = useState<number | null>(null);
    const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isOpen = (i: number) => (level === 0 ? forceOpenIndex === i : hoverIndex === i);

    const clearHoverTimer = () => {
        if (hoverTimer.current) {
            clearTimeout(hoverTimer.current);
            hoverTimer.current = null;
        }
    };

    const handleTopClick = (event: ReactMouseEvent, i: number, hasChildren: boolean) => {
        if (!hasChildren || !setForceOpenIndex) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        onTopItemClick?.(i, hasChildren);
        onBeforeToggle?.();
        setForceOpenIndex(forceOpenIndex === i ? null : i);
    };

    const handleMouseEnter = (i: number, hasChildren: boolean) => {
        clearHoverTimer();
        onMenuPointerEnter?.();

        if (level === 0 && hasChildren && setForceOpenIndex) {
            setForceOpenIndex(i);
            return;
        }

        if (level > 0) {
            setHoverIndex(i);
        }
    };

    const handleMouseLeave = (hasChildren: boolean) => {
        clearHoverTimer();
        onMenuPointerLeave?.();

        if (level > 0) {
            hoverTimer.current = setTimeout(() => {
                setHoverIndex(null);
            }, 160);
        }

        if (level === 0 && hasChildren) {
            // closing handled by parent hover state
        }
    };

    return (
        <div
            className={cx(
                level === 0 ? "items" : "subitems-grid",
                level > 0 && gridLayout === "three-column" && "subitems-grid-three",
            )}
        >
            {items.map((item, i) => {
                const hasChildren = (item.items?.length ?? 0) > 0;
                const itemOpen = isOpen(i);

                return (
                    <div
                        key={`${item.label}-${i}`}
                        className={cx(level === 0 ? "item" : "subitem-row")}
                        onMouseEnter={() => handleMouseEnter(i, hasChildren)}
                        onMouseLeave={() => handleMouseLeave(hasChildren)}
                    >
                        {level === 0 && hasChildren ? (
                            <button
                                type="button"
                                className={cx("link", "submenu", "link-icon", itemOpen && "link-active")}
                                aria-expanded={itemOpen}
                                onClick={(event) => handleTopClick(event, i, hasChildren)}
                                onPointerDown={(event) => event.stopPropagation()}
                            >
                                <LinkContent item={item} isOpen={itemOpen} />
                            </button>
                        ) : item.url ? (
                            <a
                                className={cx("link", level === 0 && "link-icon")}
                                onClick={() => {
                                    onBeforeToggle?.();
                                    onClick?.();
                                    setForceOpenIndex?.(null);
                                }}
                                href={typeof item.url === "string" ? item.url : "#"}
                            >
                                <LinkContent item={item} />
                            </a>
                        ) : (
                            <span className={cx("link", "submenu")}>
                                <LinkContent item={item} />
                            </span>
                        )}

                        {hasChildren && level > 0 && (
                            <div className={cx("subitems-wrapper", "subitems-nested", itemOpen && "open")}>
                                <NavbarList
                                    items={item.items!}
                                    level={level + 1}
                                    onClick={onClick}
                                    forceOpenIndex={forceOpenIndex}
                                    setForceOpenIndex={setForceOpenIndex}
                                />
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export const Navbar = ({ className, onClick, data }: NavbarProps) => {
    const ref = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const menuHoverRef = useRef(false);
    const ignoreNextOutsideClickRef = useRef(false);
    const [forceOpenIndex, setForceOpenIndex] = useState<number | null>(null);
    const [dropdownTop, setDropdownTop] = useState(68);
    const [mounted, setMounted] = useState(false);
    const pathname = usePathname() ?? "/";

    const openItem = forceOpenIndex !== null ? data[forceOpenIndex] : null;

    const clearCloseTimer = () => {
        if (closeTimer.current) {
            clearTimeout(closeTimer.current);
            closeTimer.current = null;
        }
    };

    const closeMenu = () => {
        clearCloseTimer();
        menuHoverRef.current = false;
        setForceOpenIndex(null);
    };

    useEffect(() => {
        closeMenu();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- close on every route change only
    }, [pathname]);

    const scheduleClose = () => {
        clearCloseTimer();
        closeTimer.current = setTimeout(() => {
            if (!menuHoverRef.current) {
                closeMenu();
            }
        }, 260);
    };

    const handleMenuPointerEnter = () => {
        menuHoverRef.current = true;
        clearCloseTimer();
    };

    const handleMenuPointerLeave = () => {
        menuHoverRef.current = false;
        scheduleClose();
    };

    const handleTopItemClick = () => {
        handleMenuPointerEnter();
    };

    const handleBeforeToggle = () => {
        ignoreNextOutsideClickRef.current = true;
        window.setTimeout(() => {
            ignoreNextOutsideClickRef.current = false;
        }, 0);
    };

    useEffect(() => {
        setMounted(true);
    }, []);

    const syncDropdownTop = () => {
        const header = document.querySelector("header");
        if (!header) {
            return;
        }

        const rect = header.getBoundingClientRect();
        setDropdownTop(Math.max(56, Math.round(rect.bottom + 8)));
    };

    useEffect(() => {
        if (forceOpenIndex === null) {
            return;
        }

        syncDropdownTop();
        window.addEventListener("resize", syncDropdownTop);
        window.addEventListener("scroll", syncDropdownTop, { passive: true });

        return () => {
            window.removeEventListener("resize", syncDropdownTop);
            window.removeEventListener("scroll", syncDropdownTop);
        };
    }, [forceOpenIndex]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (ignoreNextOutsideClickRef.current) {
                ignoreNextOutsideClickRef.current = false;
                return;
            }

            const target = event.target as Node;

            if (ref.current?.contains(target)) {
                return;
            }

            if (dropdownRef.current?.contains(target)) {
                return;
            }

            closeMenu();
        };

        document.addEventListener("click", handleClickOutside);

        return () => {
            document.removeEventListener("click", handleClickOutside);
            clearCloseTimer();
        };
    }, []);

    const dropdownPortal =
        mounted && openItem?.items?.length
            ? createPortal(
                  <div
                      ref={dropdownRef}
                      className={cx(
                          "dropdown-portal",
                          "bg-liquid-glass",
                          "glass-specular",
                          openItem.menuLayout === "three-column" && "dropdown-portal-wide",
                      )}
                      style={{ top: `${dropdownTop}px` }}
                      data-fixed-overlay="true"
                      onMouseEnter={handleMenuPointerEnter}
                      onMouseLeave={handleMenuPointerLeave}
                      onClick={(event) => event.stopPropagation()}
                  >
                      <div className={cx("subitems-wrapper", "subitems-wrapper-portal")}>
                          <NavbarList
                              items={openItem.items}
                              level={1}
                              gridLayout={openItem.menuLayout ?? "two-column"}
                              onClick={onClick}
                              forceOpenIndex={forceOpenIndex}
                              setForceOpenIndex={setForceOpenIndex}
                              onMenuPointerEnter={handleMenuPointerEnter}
                              onMenuPointerLeave={handleMenuPointerLeave}
                          />
                      </div>
                  </div>,
                  document.body,
              )
            : null;

    return (
        <>
            <div className={cx("navbar", className)} ref={ref}>
                <NavbarList
                    items={data}
                    onClick={onClick}
                    forceOpenIndex={forceOpenIndex}
                    setForceOpenIndex={setForceOpenIndex}
                    onMenuPointerEnter={handleMenuPointerEnter}
                    onMenuPointerLeave={handleMenuPointerLeave}
                    onTopItemClick={handleTopItemClick}
                    onBeforeToggle={handleBeforeToggle}
                />
            </div>
            {dropdownPortal}
        </>
    );
};
